/**
 * TaskReportsDialog — analytics des tâches : overview + throughput + burndown.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart3, TrendingDown, TrendingUp, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";

const STATUS_LABEL = { todo: "À faire", in_progress: "En cours", done: "Fait", cancelled: "Annulée" };
const STATUS_COLOR = { todo: "#94a3b8", in_progress: "#3b82f6", done: "#10b981", cancelled: "#f43f5e" };
const PRIO_LABEL = { low: "Faible", medium: "Normale", high: "Haute", urgent: "Urgente" };
const PRIO_COLOR = { low: "#94a3b8", medium: "#3b82f6", high: "#f59e0b", urgent: "#ef4444" };

export default function TaskReportsDialog({ open, onOpenChange }) {
  const [overview, setOverview] = useState(null);
  const [throughput, setThroughput] = useState(null);
  const [burndown, setBurndown] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoadError(null);
    // Fetches indépendants : si un endpoint échoue, on ne bloque pas les autres.
    (async () => {
      try { const r = await api.get("/tasks/reports/overview"); setOverview(r.data); }
      catch (e) { setLoadError(apiError(e)); toast.error("Rapports (overview) : " + apiError(e)); }
    })();
    (async () => {
      try { const r = await api.get("/tasks/reports/throughput?days=14"); setThroughput(r.data); }
      catch (e) { toast.error("Rapports (throughput) : " + apiError(e)); }
    })();
    (async () => {
      try { const r = await api.get("/tasks/reports/burndown?days=14"); setBurndown(r.data); }
      catch (e) { toast.error("Rapports (burndown) : " + apiError(e)); }
    })();
  }, [open]);

  const pieByStatus = overview
    ? Object.entries(overview.by_status).map(([k, v]) => ({ name: STATUS_LABEL[k], value: v, color: STATUS_COLOR[k] }))
    : [];
  const barByPriority = overview
    ? Object.entries(overview.by_priority).map(([k, v]) => ({ name: PRIO_LABEL[k], value: v, color: PRIO_COLOR[k] }))
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="reports-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5" />Rapports & Analytics</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>

        {!overview ? (
          <div className="py-12 text-center text-slate-400" data-testid="reports-loading">
            {loadError ? (
              <div className="text-red-600">
                <p className="font-medium">Erreur de chargement</p>
                <p className="text-xs mt-1">{loadError}</p>
              </div>
            ) : "Chargement…"}
          </div>
        ) : (
          <div className="space-y-6">
            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <KpiCard label="Total" value={overview.total} icon={<CheckCircle2 className="w-4 h-4" />} />
              <KpiCard label="Créées (7j)" value={overview.created_this_week} icon={<TrendingUp className="w-4 h-4 text-blue-500" />} />
              <KpiCard label="Faites (7j)" value={overview.done_this_week} icon={<CheckCircle2 className="w-4 h-4 text-emerald-500" />} />
              <KpiCard label="En retard" value={overview.overdue} icon={<AlertTriangle className="w-4 h-4 text-red-500" />} tone={overview.overdue > 0 ? "red" : ""} />
              <KpiCard label="Complétion" value={`${overview.completion_rate}%`} icon={<TrendingDown className="w-4 h-4 text-emerald-500" />} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Pie by status */}
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm font-semibold mb-2">Répartition par statut</p>
                  <div className="h-56">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={pieByStatus} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80} label>
                          {pieByStatus.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Bar by priority */}
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm font-semibold mb-2">Par priorité</p>
                  <div className="h-56">
                    <ResponsiveContainer>
                      <BarChart data={barByPriority}>
                        <XAxis dataKey="name" fontSize={11} />
                        <YAxis allowDecimals={false} fontSize={11} />
                        <Tooltip />
                        <Bar dataKey="value">
                          {barByPriority.map((e, i) => <Cell key={i} fill={e.color} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Throughput line */}
              {throughput && (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm font-semibold mb-2">Throughput 14 jours</p>
                    <p className="text-xs text-slate-500 mb-2">Créées: {throughput.totals.created} · Faites: {throughput.totals.completed}</p>
                    <div className="h-56">
                      <ResponsiveContainer>
                        <LineChart data={throughput.series}>
                          <XAxis dataKey="date" fontSize={10} tickFormatter={d => d.slice(5)} />
                          <YAxis allowDecimals={false} fontSize={11} />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="created" stroke="#3b82f6" name="Créées" strokeWidth={2} />
                          <Line type="monotone" dataKey="completed" stroke="#10b981" name="Faites" strokeWidth={2} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Burndown */}
              {burndown && (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm font-semibold mb-2">Burndown (reste à faire)</p>
                    <p className="text-xs text-slate-500 mb-2">Ouvert actuel: {burndown.current_open}</p>
                    <div className="h-56">
                      <ResponsiveContainer>
                        <LineChart data={burndown.series}>
                          <XAxis dataKey="date" fontSize={10} tickFormatter={d => d.slice(5)} />
                          <YAxis allowDecimals={false} fontSize={11} />
                          <Tooltip />
                          <Line type="monotone" dataKey="remaining" stroke="#f59e0b" strokeWidth={2} name="Restant" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {overview.top_assignees.length > 0 && (
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm font-semibold mb-2">Top assignés</p>
                  <div className="flex flex-wrap gap-2">
                    {overview.top_assignees.map(a => (
                      <Badge key={a.name} variant="outline" className="text-xs" data-testid={`assignee-${a.name}`}>
                        {a.name} · {a.count}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KpiCard({ label, value, icon, tone }) {
  const toneCls = tone === "red" ? "border-red-200 bg-red-50" : "border-slate-200 bg-white";
  return (
    <div className={`${toneCls} rounded-lg border p-3`} data-testid={`kpi-${label}`}>
      <div className="flex items-center gap-1.5 text-xs text-slate-500">{icon}{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
